import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Textarea } from '../../ui/Textarea';
import { Select } from '../../ui/Select';

// Types
interface TeamData {
  name: string;
  description: string;
  color: string;
  assignment_strategy: string;
  max_concurrent_chats: number;
  auto_assign_enabled: boolean;
  timezone: string;
  working_hours: Record<string, any>;
  agent_invites: Array<{
    email: string;
    role: string;
    display_name: string;
  }>;
  chatbot_assignments: Array<{
    chatbot_id: string;
    priority: number;
  }>;
}

interface StepIndicatorProps {
  steps: Array<{ id: number; title: string }>;
  currentStep: number;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
          step.id <= currentStep 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-200 text-gray-600'
        }`}>
          {step.id}
        </div>
        <div className={`ml-2 mr-4 text-sm font-medium ${
          step.id <= currentStep ? 'text-blue-600' : 'text-gray-500'
        }`}>
          {step.title}
        </div>
        {index < steps.length - 1 && (
          <div className={`flex-1 h-px mx-4 ${
            step.id < currentStep ? 'bg-blue-600' : 'bg-gray-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Step 1: Team Info
interface TeamInfoStepProps {
  data: Partial<TeamData>;
  onNext: (data: Partial<TeamData>) => void;
}

const TeamInfoStep: React.FC<TeamInfoStepProps> = ({ data, onNext }) => {
  const [formData, setFormData] = useState({
    name: data.name || '',
    description: data.description || '',
    color: data.color || '#3b82f6'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onNext(formData);
    }
  };

  const colorOptions = [
    { value: '#3b82f6', label: '🔵 Blue', color: '#3b82f6' },
    { value: '#ef4444', label: '🔴 Red', color: '#ef4444' },
    { value: '#10b981', label: '🟢 Green', color: '#10b981' },
    { value: '#f59e0b', label: '🟡 Yellow', color: '#f59e0b' },
    { value: '#8b5cf6', label: '🟣 Purple', color: '#8b5cf6' },
    { value: '#06b6d4', label: '🔵 Cyan', color: '#06b6d4' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Customer Support Team"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this team handles..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team Color
            </label>
            <div className="grid grid-cols-3 gap-3">
              {colorOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: option.value })}
                  className={`flex items-center p-3 border rounded-lg hover:bg-gray-50 ${
                    formData.color === option.value 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300'
                  }`}
                >
                  <div 
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: option.color }}
                  />
                  <span className="text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit">
              Next: Settings
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Step 2: Team Settings
interface TeamSettingsStepProps {
  data: Partial<TeamData>;
  onNext: (data: Partial<TeamData>) => void;
  onBack: () => void;
}

const TeamSettingsStep: React.FC<TeamSettingsStepProps> = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    assignment_strategy: data.assignment_strategy || 'round_robin',
    max_concurrent_chats: data.max_concurrent_chats || 3,
    auto_assign_enabled: data.auto_assign_enabled !== false,
    timezone: data.timezone || 'UTC'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext(formData);
  };

  const assignmentStrategies = [
    { value: 'round_robin', label: 'Round Robin', description: 'Assign conversations to agents in rotation' },
    { value: 'load_based', label: 'Load Based', description: 'Assign to agent with fewest active chats' },
    { value: 'manual', label: 'Manual', description: 'Team lead assigns conversations manually' }
  ];

  const timezones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US)' },
    { value: 'America/Chicago', label: 'Central Time (US)' },
    { value: 'America/Denver', label: 'Mountain Time (US)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { value: 'Europe/London', label: 'London Time' },
    { value: 'Europe/Paris', label: 'Central European Time' },
    { value: 'Asia/Tokyo', label: 'Japan Time' },
    { value: 'Asia/Shanghai', label: 'China Time' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Strategy
            </label>
            <div className="space-y-3">
              {assignmentStrategies.map((strategy) => (
                <label key={strategy.value} className="flex items-start">
                  <input
                    type="radio"
                    name="assignment_strategy"
                    value={strategy.value}
                    checked={formData.assignment_strategy === strategy.value}
                    onChange={(e) => setFormData({ ...formData, assignment_strategy: e.target.value })}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{strategy.label}</div>
                    <div className="text-sm text-gray-600">{strategy.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Concurrent Chats per Agent
            </label>
            <Select
              value={formData.max_concurrent_chats.toString()}
              onChange={(value) => setFormData({ ...formData, max_concurrent_chats: parseInt(value) })}
              options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => ({
                value: num.toString(),
                label: `${num} conversations`
              }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <Select
              value={formData.timezone}
              onChange={(value) => setFormData({ ...formData, timezone: value })}
              options={timezones.map(tz => ({
                value: tz.value,
                label: tz.label
              }))}
            />
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.auto_assign_enabled}
                onChange={(e) => setFormData({ ...formData, auto_assign_enabled: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Enable automatic assignment of new conversations
              </span>
            </label>
            <p className="mt-1 text-sm text-gray-600">
              When enabled, new conversations will be automatically assigned to available agents
            </p>
          </div>

          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button type="submit">
              Next: Invite Agents
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Step 3: Invite Agents
interface InviteAgentsStepProps {
  data: Partial<TeamData>;
  onNext: (data: Partial<TeamData>) => void;
  onBack: () => void;
}

const InviteAgentsStep: React.FC<InviteAgentsStepProps> = ({ data, onNext, onBack }) => {
  const [invites, setInvites] = useState(data.agent_invites || []);
  const [newInvite, setNewInvite] = useState({
    email: '',
    role: 'agent',
    display_name: ''
  });

  const addInvite = () => {
    if (newInvite.email && newInvite.display_name) {
      setInvites([...invites, { ...newInvite }]);
      setNewInvite({ email: '', role: 'agent', display_name: '' });
    }
  };

  const removeInvite = (index: number) => {
    setInvites(invites.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext({ agent_invites: invites });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Team Members</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium text-gray-900 mb-3">Add New Team Member</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Input
                  placeholder="Email address"
                  type="email"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                />
              </div>
              <div>
                <Input
                  placeholder="Display name"
                  value={newInvite.display_name}
                  onChange={(e) => setNewInvite({ ...newInvite, display_name: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={newInvite.role}
                  onChange={(value) => setNewInvite({ ...newInvite, role: value })}
                >
                  <option value="agent">Agent</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="supervisor">Supervisor</option>
                </Select>
                <Button 
                  type="button" 
                  onClick={addInvite}
                  disabled={!newInvite.email || !newInvite.display_name}
                  className="px-6"
                >
                  Add
                </Button>
              </div>
            </div>
          </div>

          {invites.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Team Members to Invite</h4>
              <div className="space-y-2">
                {invites.map((invite, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{invite.display_name}</div>
                      <div className="text-sm text-gray-600">{invite.email} • {invite.role}</div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeInvite(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="text-blue-600 mr-3">ℹ️</div>
              <div>
                <h4 className="font-medium text-blue-900">Team Member Roles</h4>
                <div className="mt-2 text-sm text-blue-800">
                  <p><strong>Agent:</strong> Can respond to assigned conversations</p>
                  <p><strong>Team Lead:</strong> Can manage team members and assign conversations</p>
                  <p><strong>Supervisor:</strong> Can view all team analytics and performance</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button type="submit">
              Next: Assign Chatbots
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Step 4: Assign Chatbots
interface AssignChatbotsStepProps {
  data: Partial<TeamData>;
  onFinish: (data: TeamData) => void;
  onBack: () => void;
  chatbots: Array<{ id: string; name: string; description: string }>;
  isEditing?: boolean;
}

const AssignChatbotsStep: React.FC<AssignChatbotsStepProps> = ({ 
  data, 
  onFinish, 
  onBack, 
  chatbots = [],
  isEditing = false
}) => {
  // Ensure chatbots is always an array
  const safeChatbots = Array.isArray(chatbots) ? chatbots : [];
  const [assignments, setAssignments] = useState(data.chatbot_assignments || []);

  const toggleChatbot = (chatbotId: string) => {
    const existing = assignments.find(a => a.chatbot_id === chatbotId);
    if (existing) {
      setAssignments(assignments.filter(a => a.chatbot_id !== chatbotId));
    } else {
      setAssignments([...assignments, { chatbot_id: chatbotId, priority: 1 }]);
    }
  };

  const updatePriority = (chatbotId: string, priority: number) => {
    setAssignments(assignments.map(a => 
      a.chatbot_id === chatbotId ? { ...a, priority } : a
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFinish({ ...data, chatbot_assignments: assignments } as TeamData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assign Chatbots</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Select which chatbots this team will handle. You can assign priority levels for complex routing.
            </p>
            
            {safeChatbots.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No chatbots available. Create a chatbot first to assign it to teams.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {safeChatbots.map((chatbot) => {
                  const assignment = assignments.find(a => a.chatbot_id === chatbot.id);
                  const isAssigned = !!assignment;
                  
                  return (
                    <div key={chatbot.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            onChange={() => toggleChatbot(chatbot.id)}
                            className="mt-1 mr-3"
                          />
                          <div>
                            <h4 className="font-medium text-gray-900">{chatbot.name}</h4>
                            <p className="text-sm text-gray-600">{chatbot.description}</p>
                          </div>
                        </div>
                        
                        {isAssigned && (
                          <div className="ml-4 min-w-[160px]">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                            <Select
                              value={assignment.priority.toString()}
                              onChange={(value) => updatePriority(chatbot.id, parseInt(value))}
                              options={[
                                { value: "1", label: "Primary (1)" },
                                { value: "2", label: "Backup (2)" },
                                { value: "3", label: "Overflow (3)" }
                              ]}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {assignments.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <div className="text-green-600 mr-3">✅</div>
                <div>
                  <h4 className="font-medium text-green-900">
                    {assignments.length} chatbot{assignments.length !== 1 ? 's' : ''} assigned
                  </h4>
                  <p className="text-sm text-green-800">
                    Your team will handle conversations from these chatbots based on the assignment strategy.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button type="submit">
              {isEditing ? 'Update Team' : 'Create Team'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Main Wizard Component
interface CreateTeamWizardProps {
  onComplete: (teamData: TeamData) => void;
  onCancel: () => void;
  chatbots?: Array<{ id: string; name: string; description: string }>;
  initialData?: any;
  isEditing?: boolean;
}

const CreateTeamWizard: React.FC<CreateTeamWizardProps> = ({ 
  onComplete, 
  onCancel, 
  chatbots = [],
  initialData,
  isEditing = false
}) => {
  const [step, setStep] = useState(1);
  const [teamData, setTeamData] = useState<Partial<TeamData>>(initialData || {});

  const steps = [
    { id: 1, title: "Team Info" },
    { id: 2, title: "Settings" },
    { id: 3, title: "Invite Agents" },
    { id: 4, title: "Assign Chatbots" }
  ];

  const handleStepData = (stepData: Partial<TeamData>) => {
    setTeamData({ ...teamData, ...stepData });
    setStep(step + 1);
  };

  const handleFinish = (finalData: TeamData) => {
    onComplete(finalData);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Team' : 'Create New Team'}
        </h1>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <StepIndicator steps={steps} currentStep={step} />

      {step === 1 && (
        <TeamInfoStep 
          data={teamData} 
          onNext={handleStepData}
        />
      )}

      {step === 2 && (
        <TeamSettingsStep 
          data={teamData}
          onNext={handleStepData}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <InviteAgentsStep 
          data={teamData}
          onNext={handleStepData}
          onBack={() => setStep(2)}
        />
      )}

      {step === 4 && (
        <AssignChatbotsStep 
          data={teamData}
          onFinish={handleFinish}
          onBack={() => setStep(3)}
          chatbots={chatbots}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default CreateTeamWizard;